/**
 * ONE footer for every pop-up and drawer (KAN-948 / RD-UI-155).
 *
 * Before this file the footer was drawn five ways: buttons 34 / 38 / 40 / 42 / 44px tall,
 * Cancel as a plain word in some and an outlined box in others, and a third choice stacked
 * under the main action. Everything a footer needs now lives here:
 *
 *   - `overlayFooterRowStyle({ isTouch, sectioned })`  the row (gap 12, never grows).
 *   - `overlayFooterButtonStyle({ isTouch, kind })`    `cancel` | `primary` | `danger`.
 *   - `overlayFooterLinkStyle({ isTouch })`            the third choice, a text link ABOVE the row.
 *   - `overlayFooterPadding({ isTouch })`              the inset of a sectioned footer / a drawer's footer.
 *
 * Rules (do not re-decide them per modal):
 *   - Height 38 on web, 44 under a finger. SIZE follows `isTouch` (KAN-970: a coarse pointer OR
 *     under 1024), never the width flags. `minHeight`, never `height`, so a label that wraps grows.
 *   - Cancel is on the LEFT, the main action on the RIGHT. Cancel is OUTLINED everywhere (white
 *     fill, 1px neutral border) - never a bare word, never a grey fill.
 *   - A third choice is a text link above the row (`overlayFooterLinkStyle`), never a third button.
 *   - Padding: web 16 / 24, touch 12 / 16; the bottom edge on touch is `max(12px, safe-area
 *     inset)` - never their sum (a 12px pad ON TOP of the inset left a 46px dead band, KAN-768).
 *   - Sectioned footers (the panel has `padding: 0`: wizards, notice, export, person-action) draw
 *     their own top border and `marginTop: 0`; padded-panel modals use the plain row
 *     (`modalPinnedFooterRow` underneath: 16px above, never grows - KAN-861). Drawers use `bare`
 *     because `DrawerShell`'s footer wrapper already owns the padding and the hairline.
 *   - A button that hands over to `ActionBeatFill` passes the SAME style as `confirmStyle` /
 *     `cancelStyle`, so the box is identical before and after the beat (`ActionBeatFill` takes
 *     these as its defaults too).
 *
 * Adopt it with `useOverlayFooter()` (components/common/overlay) or call the helpers directly.
 * Guard: components/common/overlay/overlayFooter.guard.test.js (a ratchet that may only shrink).
 */

import { foundation } from './tokens';
import { status } from './theme/semanticTokens';
import { modalPinnedFooterRow, overlayActionDisabled } from './overlayStyles';

const { color, spacePx, radii, shadow, fontSize, fontWeight, fontFamily, lineHeight } = foundation;

/** Gap between footer buttons - the one number. */
export const OVERLAY_FOOTER_GAP_PX = 12;

/** Button height: web 38, under a finger 44. */
export const OVERLAY_FOOTER_BUTTON_PX = { web: 38, touch: 44 };

/** Sectioned-footer inset in px: `y` top/bottom, `x` left/right. */
export const OVERLAY_FOOTER_PAD_PX = { web: { y: 16, x: 24 }, touch: { y: 12, x: 16 } };

export const overlayFooterButtonHeight = (isTouch) => (
  isTouch ? OVERLAY_FOOTER_BUTTON_PX.touch : OVERLAY_FOOTER_BUTTON_PX.web
);

/**
 * Inset of a footer that draws its own padding (sectioned modal footer, DrawerShell footer).
 * A centred pop-up floats between the app header and the nav, never at the home bar, so on touch its
 * bottom is the touch side padding (16) with no safe-area term. A footer whose bottom touches the
 * screen edge (a drawer) passes `atScreenEdge`: its bottom is `max(12px, env(safe-area-inset-bottom))` -
 * the inset REPLACES the gutter when it is bigger, it is never added to it.
 */
export function overlayFooterPadding({ isTouch = false, atScreenEdge = false } = {}) {
  const pad = isTouch ? OVERLAY_FOOTER_PAD_PX.touch : OVERLAY_FOOTER_PAD_PX.web;
  let paddingBottom = `${pad.y}px`;
  if (isTouch) {
    paddingBottom = atScreenEdge
      ? `max(${pad.y}px, env(safe-area-inset-bottom, 0px))`
      : `${OVERLAY_FOOTER_PAD_PX.touch.x}px`;
  }
  return {
    paddingTop: `${pad.y}px`,
    paddingRight: `${pad.x}px`,
    paddingBottom,
    paddingLeft: `${pad.x}px`,
  };
}

/**
 * The button row.
 * @param {object} [o]
 * @param {boolean} [o.isTouch]    from `useResponsiveLayout()`.
 * @param {boolean} [o.sectioned]  edge-to-edge footer of a `padding: 0` panel: padding + top border, `marginTop: 0`.
 * @param {boolean} [o.bare]       just the row (gap, no margin / padding / border) - the host's wrapper owns the inset (drawers).
 */
export function overlayFooterRowStyle({ isTouch = false, sectioned = false, bare = false } = {}) {
  const row = {
    ...modalPinnedFooterRow, // display flex, flex 0 0 auto, never grows, marginTop 16
    flexDirection: 'row',
    alignItems: 'stretch',
    // The extreme-case safety of every footer row: a pathologically long label stacks, never clips.
    flexWrap: 'wrap',
    gap: `${OVERLAY_FOOTER_GAP_PX}px`,
    boxSizing: 'border-box',
  };
  if (bare) return { ...row, marginTop: 0 };
  if (!sectioned) return row;
  return {
    ...row,
    marginTop: 0,
    ...overlayFooterPadding({ isTouch }),
    borderTop: `1px solid ${color.neutral.border}`,
    background: color.neutral.surfaceElevated,
  };
}

const KINDS = {
  cancel: {
    background: color.neutral.surface,
    backgroundColor: color.neutral.surface,
    color: color.neutral.text,
    border: `1px solid ${color.neutral.border}`,
  },
  primary: {
    background: color.brand.primary,
    backgroundColor: color.brand.primary,
    color: color.brand.onPrimary,
    border: `1px solid ${color.brand.primary}`,
    boxShadow: shadow.xs,
  },
  danger: {
    background: status.dangerFill,
    backgroundColor: status.dangerFill,
    color: color.brand.onPrimary,
    border: `1px solid ${status.dangerFill}`,
    boxShadow: shadow.xs,
  },
};

/**
 * One footer button. `kind`: `cancel` (outlined) | `primary` | `danger`.
 * `disabled` applies the canonical muted look (filled kinds) / a dimmed outline (cancel);
 * the caller still sets the real `disabled` attribute.
 * `equal` splits the row 50/50 (`flex: 1 1 0%`) instead of sizing to the label - the drawer footers
 * (which hand over to `ActionBeatFill`, whose idle columns are `1fr 1fr`, so the width never jumps).
 */
export function overlayFooterButtonStyle({
  isTouch = false, kind = 'primary', disabled = false, equal = false,
} = {}) {
  const tone = KINDS[kind] || KINDS.primary;
  return {
    // Content-sized, then grows equally - a long Spanish label gets the width it needs.
    flex: equal ? '1 1 0%' : '1 1 auto',
    boxSizing: 'border-box',
    minHeight: overlayFooterButtonHeight(isTouch),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacePx[2], // room for an icon or a spinner beside the label
    // Touch trades 4px of side padding for width so "Cancelar" + "Guardar cambios" always share
    // one row on a 320px phone (see overlayFooter.test.js, "fits one row at 320").
    padding: `${spacePx[2]} ${isTouch ? spacePx[3] : spacePx[4]}`,
    whiteSpace: 'nowrap',
    fontFamily: fontFamily.sans,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.tight,
    borderRadius: radii.md,
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    ...tone,
    ...(disabled
      ? (kind === 'cancel' ? { opacity: 0.55, cursor: 'not-allowed' } : overlayActionDisabled)
      : null),
  };
}

/** The third choice: a quiet text link in its own centred line ABOVE the row. */
export function overlayFooterLinkStyle({ isTouch = false } = {}) {
  return {
    alignSelf: 'center',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: isTouch ? OVERLAY_FOOTER_BUTTON_PX.touch : 32,
    padding: `${spacePx[1]} ${spacePx[3]}`,
    background: 'none',
    border: 'none',
    color: color.brand.text,
    fontFamily: fontFamily.sans,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.tight,
    textAlign: 'center',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  };
}

/**
 * The block that holds the link above the row (and the row itself): link on top, 4px, then the row.
 * Spread the row style inside it with `marginTop: 0`.
 */
export function overlayFooterStackStyle() {
  return {
    ...modalPinnedFooterRow,
    display: 'flex',
    flexDirection: 'column',
    gap: spacePx[1],
    alignItems: 'stretch',
  };
}
